<template>
  <div class="modal fade" id="audioFolderModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">选择文件夹或压缩包</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" v-if="!isInitial"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <p>请选择包含多个音频子文件夹的项目文件夹，或直接选择一个包含音频文件/子文件夹的项目压缩包（.zip）。</p>
            <p class="text-muted mb-1">• 文件夹选择需要浏览器支持 File System Access API；若不可用，请改用“选择项目压缩包”。</p>
            <p class="text-muted mb-1">• 支持的音频格式：.mp3 / .wav </p>
            <p class="text-muted">• 将自动扫描：根目录下的音频 + 各子文件夹中的音频。若根目录与子文件夹同时存在音频，将全部纳入“可用音频列表”。</p>
          </div>
          
          <div class="mb-3">
            <div class="d-flex align-items-center mb-2">
              <label class="form-label mb-0 me-2">项目文件夹</label>
              <span v-if="projectFolder" class="badge bg-success ms-2">已选择</span>
              <span v-else-if="sourceType === 'zip' && zipFileName" class="badge bg-success ms-2">已选择ZIP</span>
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <button class="btn btn-outline-primary" @click="selectProjectFolder" :disabled="!canPickDirectory">
                选择项目文件夹
              </button>
              <button class="btn btn-outline-secondary" @click="triggerZipPicker">
                选择项目压缩包
              </button>
              <input type="file" ref="zipInput" accept=".zip" style="display:none" @change="handleZipChange" />
              <span v-if="projectFolder" class="align-self-center text-truncate">
                {{ projectFolderName }}
              </span>
              <span v-else-if="zipFileName" class="align-self-center text-truncate" :title="zipFileName">
                {{ zipFileName }}
              </span>
            </div>
            <div v-if="!canPickDirectory" class="form-text text-warning mt-1">
              当前环境不支持选择文件夹，请使用“选择项目压缩包”（.zip）进行导入。
            </div>
          </div>

          <!-- 显示检测到的子文件夹 -->
          <div v-if="subFolders.length > 0" class="mb-3">
            <h6>检测到的子文件夹：</h6>
            <div class="row">
              <div class="col-md-6" v-for="(subFolder, index) in subFolders" :key="index">
                <div class="card mb-2">
                  <div class="card-body p-2">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{{ subFolder.name }}</strong>
                        <br>
                        <small class="text-muted">{{ subFolder.audioCount }} 个音频文件</small>
                      </div>
                      <span class="badge bg-info">{{ subFolder.audioCount }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="alert alert-warning" v-if="isInitial">
            <strong>提示：</strong> 首次加载时需要选择项目文件夹才能使用音频功能。
          </div>

          <div class="alert alert-info" v-if="(projectFolder || sourceType === 'zip') && subFolders.length === 0 && rootAudioCount === 0">
            <strong>提示：</strong> 未检测到任何音频文件。请确认所选文件夹/压缩包内包含受支持格式的音频文件。
          </div>

          <div class="alert alert-secondary" v-if="rootAudioCount > 0">
            <strong>根目录音频：</strong> 在根目录检测到 {{ rootAudioCount }} 个音频文件。
          </div>
          <div class="alert alert-light" v-if="parsing">
            正在解析所选的文件夹/压缩包，请稍候…
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" v-if="!isInitial">取消</button>
          <button type="button" class="btn btn-primary" @click="confirmFolders" :disabled="!canConfirm">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JSZip from 'jszip';
export default {
  name: 'AudioFolderModal',
  emits: ['folderSelected'],
  data() {
    return {
      projectFolder: null,
      subFolders: [],
      modal: null,
      isInitial: true,
      canPickDirectory: 'showDirectoryPicker' in window,
      sourceType: null, // 'directory' | 'zip'
      zipFileName: '',
      rootAudioCount: 0,
      parsing: false,
      zipEntries: null, // JSZip entries map (files object)
      zipAudioPaths: [], // audio file paths within the zip (normalized with '/')
      zipConfigFiles: [] // json configs detected in zip
    };
  },
  computed: {
    projectFolderName() {
      return this.projectFolder ? this.projectFolder.name : '';
    },
    canConfirm() {
      // 允许：已选文件夹或ZIP，且（存在子文件夹音频 或 根目录音频）
      const hasSource = !!this.projectFolder || this.sourceType === 'zip';
      const hasAudio = (this.subFolders.length > 0) || (this.rootAudioCount > 0);
      return hasSource && hasAudio && !this.parsing;
    }
  },
  mounted() {
    // 初始化Bootstrap模态框，确保在DOM完全加载后执行
    this.$nextTick(() => {
      // 确保引入了bootstrap
      if (typeof bootstrap !== 'undefined') {
        const modalElement = document.getElementById('audioFolderModal');
        if (modalElement) {
          this.modal = new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false
          });
        } else {
          console.error('无法找到模态框元素');
        }
      } else {
        console.error('Bootstrap未正确加载');
      }
    });
  },
  methods: {
    // 显示模态框
    show() {
      // 延迟执行以确保modal已初始化
      this.$nextTick(() => {
        if (this.modal) {
          this.isInitial = false;
          this.modal.show();
        } else {
          // 重新尝试初始化modal
          const modalElement = document.getElementById('audioFolderModal');
          if (modalElement && typeof bootstrap !== 'undefined') {
            this.modal = new bootstrap.Modal(modalElement, {
              backdrop: 'static',
              keyboard: false
            });
            this.modal.show();
          } else {
            console.error('模态框无法初始化');
            alert('模态框加载失败，请刷新页面重试');
          }
        }
      });
    },
    
    // 隐藏模态框
    hide() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    
    // 选择项目文件夹
    async selectProjectFolder() {
      try {
        this.parsing = true;
        // 使用File System Access API选择文件夹
        this.projectFolder = await window.showDirectoryPicker({
          id: 'projectFolder',
          startIn: 'desktop',
          mode: 'read'
        });
        this.sourceType = 'directory';
        this.zipFileName = '';
        // 扫描子文件夹
        await this.scanSubFolders();
      } catch (error) {
        console.error('选择项目文件夹失败:', error);
        if (error.name !== 'AbortError') {
          alert('选择项目文件夹失败: ' + error.message);
        }
      } finally {
        this.parsing = false;
      }
    },
    
    // 扫描项目文件夹中的子文件夹
    async scanSubFolders() {
      this.subFolders = [];
      this.rootAudioCount = 0;
      
      if (!this.projectFolder) return;
      
      try {
        for await (const entry of this.projectFolder.values()) {
          if (entry.kind === 'directory') {
            const audioCount = await this.countAudioFiles(entry);
            if (audioCount > 0) {
              this.subFolders.push({
                name: entry.name,
                handle: entry,
                audioCount: audioCount
              });
            }
          } else if (entry.kind === 'file') {
            // 统计根目录下的音频文件数量
            const name = entry.name.toLowerCase();
            if (this.isAudioFile(name)) {
              this.rootAudioCount++;
            }
          }
        }
        
        console.log('检测到的子文件夹:', this.subFolders);
      } catch (error) {
        console.error('扫描子文件夹失败:', error);
      }
    },
    
    // 计算文件夹中的音频文件数量
    async countAudioFiles(folderHandle) {
      let count = 0;
      const validExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.wma'];
      
      async function countRecursively(handle) {
        if (handle.kind === 'file') {
          // 检查是否为音频文件
          const name = handle.name.toLowerCase();
          if (validExtensions.some(ext => name.endsWith(ext))) {
            count++;
          }
        } else if (handle.kind === 'directory') {
          for await (const entry of handle.values()) {
            await countRecursively(entry);
          }
        }
      }
      
      await countRecursively(folderHandle);
      return count;
    },

    // 简单的音频扩展名判断
    isAudioFile(name) {
      const validExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.wma'];
      return validExtensions.some(ext => name.endsWith(ext));
    },

    async validateZipJson(entry, entryName) {
      try {
        // JSZip 使用 async('string') 读取文本内容
        const text = await entry.async('string');
        const json = JSON.parse(text);
        if (!json || typeof json !== 'object') return false;
        // 简要校验关键字段
        if (!json.defaultConfig || typeof json.defaultConfig !== 'object') return false;
        return text;
      } catch (error) {
        console.warn('校验ZIP内JSON失败，已忽略此文件:', entryName, error);
        return false;
      }
    },

    // 触发ZIP选择
    triggerZipPicker() {
      if (this.$refs.zipInput) {
        this.$refs.zipInput.value = '';
        this.$refs.zipInput.click();
      }
    },

    // 处理ZIP文件变化
    async handleZipChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.zipFileName = file.name;
      this.projectFolder = null;
      this.sourceType = 'zip';
      this.parsing = true;
      try {
        await this.parseZip(file);
      } catch (e) {
        console.error('解析ZIP失败:', e);
        alert('解析压缩包失败：' + (e?.message || '未知错误'));
        this.zipFileName = '';
        this.sourceType = null;
        this.zipEntries = null;
        this.zipAudioPaths = [];
        this.zipConfigFiles = [];
      } finally {
        this.parsing = false;
      }
    },

    // 规范化路径：统一使用 '/' 分隔符，支持 Windows 的 '\'
    normalizePath(path) {
      return path.replace(/\\/g, '/');
    },

    // 检测公共父文件夹：如果所有音频文件都在同一个顶层文件夹下，返回该文件夹名
    detectCommonParentFolder(audioPaths) {
      if (audioPaths.length === 0) return null;
      
      // 统计每个顶层文件夹包含的音频数量
      const topLevelFolders = new Set();
      let hasRootFiles = false;
      
      for (const path of audioPaths) {
        const parts = path.split('/').filter(Boolean);
        if (parts.length === 1) {
          // 文件在根目录
          hasRootFiles = true;
        } else {
          // 文件在子文件夹
          topLevelFolders.add(parts[0]);
        }
      }
      
      // 如果存在根目录文件，说明不存在公共父文件夹
      if (hasRootFiles) return null;
      
      // 如果只有一个顶层文件夹，且所有文件都在其下，则这是公共父文件夹
      if (topLevelFolders.size === 1) {
        return Array.from(topLevelFolders)[0];
      }
      
      return null;
    },

    // 使用 JSZip 解析ZIP并统计子文件夹与根目录音频
    async parseZip(file) {
      this.subFolders = [];
      this.rootAudioCount = 0;
      this.zipEntries = null;
      this.zipAudioPaths = [];
      this.zipConfigFiles = [];

      // 使用 JSZip 加载压缩包
      const zip = new JSZip();
      const zipData = await zip.loadAsync(file);
      
      // JSZip 将文件存储在 files 对象中，键为文件路径
      this.zipEntries = zipData.files;
      
      // 第一遍：收集所有音频路径（规范化后）
      const rawAudioPaths = [];
      
      // 遍历压缩包中的所有文件
      for (const [relativePath, zipEntry] of Object.entries(zipData.files)) {
        // 跳过目录
        if (zipEntry.dir) continue;
        
        // JSZip 的 relativePath 已经是字符串，直接使用
        const normalizedName = this.normalizePath(relativePath);
        const lower = normalizedName.toLowerCase();
        
        if (this.isAudioFile(lower)) {
          const parts = normalizedName.split('/').filter(Boolean);
          rawAudioPaths.push({
            original: relativePath,  // JSZip 的路径已经是规范的
            normalized: parts.join('/'),
            parts: parts,
            zipEntry: zipEntry  // 保存 zipEntry 引用用于后续读取
          });
        } else if (lower.endsWith('.json')) {
          const rawText = await this.validateZipJson(zipEntry, normalizedName);
          if (rawText) {
            this.zipConfigFiles.push({
              name: normalizedName.split('/').pop(),
              path: normalizedName,
              entryPath: relativePath,
              rawText
            });
          }
        }
      }

      // 检测公共父文件夹
      const commonParent = this.detectCommonParentFolder(
        rawAudioPaths.map(item => item.normalized)
      );

      console.log('检测到的公共父文件夹:', commonParent || '无');

      // 第二遍：根据是否存在公共父文件夹，决定使用哪一层作为分类依据
      const topFolderMap = new Map(); // key: 文件夹名（''代表根），value: 计数
      
      for (const audioPath of rawAudioPaths) {
        let targetFolder = '';
        
        if (commonParent) {
          // 存在公共父文件夹，跳过这一层，使用下一层
          if (audioPath.parts.length > 2 && audioPath.parts[0] === commonParent) {
            // 路径格式：公共父/子文件夹/文件.wav
            targetFolder = audioPath.parts[1];
          } else if (audioPath.parts.length === 2 && audioPath.parts[0] === commonParent) {
            // 路径格式：公共父/文件.wav（直接在公共父文件夹下）
            targetFolder = '';  // 归为根目录
          } else {
            // 不在公共父文件夹路径下（理论上不应该出现）
            targetFolder = audioPath.parts.length > 1 ? audioPath.parts[0] : '';
          }
        } else {
          // 不存在公共父文件夹，使用顶层文件夹
          targetFolder = audioPath.parts.length > 1 ? audioPath.parts[0] : '';
        }
        
        const prev = topFolderMap.get(targetFolder) || 0;
        topFolderMap.set(targetFolder, prev + 1);
        
        // 记录音频路径（使用规范化后的路径）
        this.zipAudioPaths.push({
          original: audioPath.original,  // 原始路径用于读取文件
          normalized: audioPath.normalized,  // 规范化路径用于显示
          targetFolder: targetFolder  // 目标文件夹
        });
      }

      // 填充subFolders与rootAudioCount
      for (const [top, count] of topFolderMap.entries()) {
        if (top === '') {
          this.rootAudioCount = count;
        } else {
          this.subFolders.push({ name: top, handle: null, audioCount: count });
        }
      }
      
      // 排序以稳定展示
      this.subFolders.sort((a, b) => a.name.localeCompare(b.name));
      
      // 如果检测到公共父文件夹，提示用户
      if (commonParent) {
        console.log(`已自动跳过公共父文件夹"${commonParent}"，使用其子文件夹进行分类`);
      }
    },
    
    // 确认文件夹选择
    confirmFolders() {
      // 对外暴露统一的事件与额外上下文，保持向后兼容
      this.$emit('folderSelected', {
        projectFolder: this.projectFolder || null,
        subFolders: this.subFolders,
        sourceType: this.sourceType || (this.projectFolder ? 'directory' : null),
        zipFileName: this.zipFileName || null,
        rootAudioCount: this.rootAudioCount,
        zipEntries: this.zipEntries,
        zipAudioPaths: this.zipAudioPaths,
        zipConfigFiles: this.zipConfigFiles
      });
      this.hide();
    }
  }
};
</script>

<style scoped>
.modal-dialog {
  max-width: 800px;
}

.card {
  transition: box-shadow 0.2s ease;
}

.card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
</style> 